import React from "react";
import {
  Box,
  SimpleGrid,
  Button,
  Text,
  Heading,
  VStack,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { TimeSlot, Physician, Location } from "../types";

interface TimeSlotPickerProps {
  date: Date;
  timeSlots: TimeSlot[];
  physicians: Physician[];
  locations: Location[];
  onSelectSlot: (slot: TimeSlot) => void;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  date,
  timeSlots,
  physicians,
  locations,
  onSelectSlot,
}) => {
  // Filter slots for the selected date
  const slotsForDate = timeSlots.filter((slot) => {
    const slotDate = new Date(slot.startTime);
    return slotDate.toDateString() === date.toDateString();
  });

  // Group slots by hour for better display
  const slotsByHour: Record<string, TimeSlot[]> = {};

  slotsForDate.forEach((slot) => {
    const hour = format(new Date(slot.startTime), "h a");
    if (!slotsByHour[hour]) {
      slotsByHour[hour] = [];
    }
    slotsByHour[hour].push(slot);
  });

  // Get physician and location info
  const getPhysicianName = (id: number) => {
    const physician = physicians.find((p) => p.id === id);
    return physician ? physician.name : "Unknown";
  };

  const getLocationName = (id: number) => {
    const location = locations.find((l) => l.id === id);
    return location ? location.name : "Unknown";
  };

  return (
    <Box>
      <Heading size="md" mb={4}>
        Available Times for {format(date, "EEEE, MMMM d, yyyy")}
      </Heading>

      {Object.keys(slotsByHour).length > 0 ? (
        Object.entries(slotsByHour).map(([hour, slots]) => (
          <Box key={hour} mb={6}>
            <Heading size="sm" mb={2}>
              {hour}
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              {slots.map((slot) => (
                <Button
                  key={slot.id}
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => onSelectSlot(slot)}
                  size="sm"
                  height="auto"
                  py={2}
                >
                  <VStack spacing={1} align="start">
                    <Text fontWeight="bold">
                      {format(new Date(slot.startTime), "h:mm a")}
                    </Text>
                    <Text fontSize="xs">
                      {getPhysicianName(slot.physicianId)}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {getLocationName(slot.locationId)}
                    </Text>
                  </VStack>
                </Button>
              ))}
            </SimpleGrid>
          </Box>
        ))
      ) : (
        <Text color="gray.500">No available appointments on this date.</Text>
      )}
    </Box>
  );
};

export default TimeSlotPicker;
